import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Archive, Search, ArrowUpRight, X, RotateCcw } from "lucide-react";
import { fmtDateOnly } from "@/lib/workflow";

const EMPTY = { pm_id: "all", customer: "all", brand: "all", project_type: "all", year: "all", completed_from: "", completed_to: "" };

function Sel({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 bg-white border-slate-200 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function ArchivePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRestore = ["admin", "project_coordinator", "project_manager"].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY);
  const [options, setOptions] = useState({ customers: [], brands: [], managers: [], projectTypes: [] });

  const load = () => {
    setLoading(true);
    api.get("/projects", { params: { status_filter: "archived", search, ...filters } })
      .then((r) => setProjects(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    Promise.all([api.get("/projects/filters"), api.get("/project-types")]).then(([f, t]) => {
      setOptions({ ...f.data, projectTypes: (t.data.project_types || []).map((x) => x.name) });
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, search]);

  const years = useMemo(() => {
    const ys = new Set((projects || []).map((p) => (p.completed_at || "").slice(0, 4)).filter(Boolean));
    return ["all", ...Array.from(ys).sort().reverse()];
  }, [projects]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const activeCount = Object.entries(filters).filter(([k, v]) => (k.includes("completed_") ? !!v : v !== "all")).length;

  const restore = async (p, e) => {
    e.stopPropagation();
    try { await api.post(`/projects/${p.project_id}/restore`); toast.success(`${p.project_number} restored`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8">
      <div>
        <p className="overline">Completed work</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" data-testid="archive-page-title"><Archive className="h-6 w-6 text-slate-400" />Archive</h1>
        <p className="text-sm text-slate-500 mt-1">Projects completed more than 30 days ago. All documents, reports and history stay fully accessible.</p>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="archive-filters">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-2">
          <div className="relative col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search number, customer, brand…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="archive-search-input" className="pl-9 bg-white border-slate-200 h-9" />
          </div>
          <Sel value={filters.year} onChange={set("year")} testId="archive-filter-year" options={years.map((y) => ({ value: y, label: y === "all" ? "All years" : y }))} />
          <Sel value={filters.pm_id} onChange={set("pm_id")} testId="archive-filter-pm" options={[{ value: "all", label: "All managers" }, ...options.managers.map((m) => ({ value: m.user_id, label: m.name }))]} />
          <Sel value={filters.customer} onChange={set("customer")} testId="archive-filter-customer" options={[{ value: "all", label: "All customers" }, ...options.customers.map((c) => ({ value: c, label: c }))]} />
          <Sel value={filters.brand} onChange={set("brand")} testId="archive-filter-brand" options={[{ value: "all", label: "All brands" }, ...options.brands.map((b) => ({ value: b, label: b }))]} />
          <Sel value={filters.project_type} onChange={set("project_type")} testId="archive-filter-type" options={[{ value: "all", label: "All types" }, ...options.projectTypes.map((t) => ({ value: t, label: t }))]} />
          <div className="flex items-center gap-1 col-span-2">
            <span className="text-xs text-slate-400">Completed</span>
            <Input type="date" value={filters.completed_from} onChange={(e) => set("completed_from")(e.target.value)} className="dash-date h-9 bg-white border-slate-200 text-xs" data-testid="archive-completed-from" />
            <span className="text-slate-400 text-xs">→</span>
            <Input type="date" value={filters.completed_to} onChange={(e) => set("completed_to")(e.target.value)} className="dash-date h-9 bg-white border-slate-200 text-xs" data-testid="archive-completed-to" />
          </div>
          {activeCount > 0 && <Button variant="ghost" className="h-9 justify-start text-slate-500" onClick={() => setFilters(EMPTY)} data-testid="archive-filters-clear"><X className="h-4 w-4 mr-1" />Clear {activeCount}</Button>}
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-500" data-testid="archive-count">{loading ? "Loading…" : `${projects.length} archived project${projects.length === 1 ? "" : "s"}`}</p>

      <div className="mt-2 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="archive-table">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="w-32">Project #</TableHead>
              <TableHead>Customer / Brand</TableHead>
              <TableHead>PM</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="w-32">Completed</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
            ) : projects.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500" data-testid="archive-empty">No archived projects yet.</TableCell></TableRow>
            ) : projects.map((p) => (
              <TableRow key={p.project_id} className="cursor-pointer row-hover" onClick={() => navigate(`/projects/${p.project_id}`)} data-testid={`archive-row-${p.project_number}`}>
                <TableCell className="font-mono text-xs">{p.project_number}</TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">{p.customer_name}<Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-200">Archived</Badge></div>
                  <div className="text-xs text-slate-500">{p.brand}</div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">{p.project_manager_name}</TableCell>
                <TableCell className="text-sm text-slate-600">{p.project_type}</TableCell>
                <TableCell className="text-sm text-slate-600">{fmtDateOnly(p.completed_at)}</TableCell>
                <TableCell className="text-right">
                  {canRestore && <Button size="sm" variant="ghost" onClick={(e) => restore(p, e)} data-testid={`archive-restore-${p.project_number}`}><RotateCcw className="h-4 w-4 mr-1" />Restore</Button>}
                  <ArrowUpRight className="h-4 w-4 text-slate-300 inline ml-1" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
